import type { FileInfo } from '@/api/file';
import type { SharedWithMeEntry } from '@/api/share';
import useFileStore from '@/utils/fileStore';
import { EditOutlined, FileAddOutlined, FolderAddOutlined, ShareAltOutlined, UploadOutlined } from '@ant-design/icons';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useHover } from 'ahooks';
import { App, Button, Input, TreeDataNode } from 'antd';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface FileTreeTitleRendererProps {
  node: any; // TreeDataNode with additional properties
  selectedKeys: React.Key[];
  expandedKeys: React.Key[];
  openFolder: (path: string) => void;
  isFileNew: (filePath: string) => boolean;
  doesFolderContainNewFiles: (folderPath: string) => boolean;
  setFileMenuVisibleKey: (key: string) => void;
  handleCreateDialogOpen: (type: 'file' | 'folder') => void;
  setUploadDialogVisible: (visible: boolean) => void;
  handleDrop: (event: React.DragEvent<HTMLSpanElement>, node?: FileInfo & TreeDataNode) => Promise<void>;
  handleDragOverInTree: (event: React.DragEvent<HTMLSpanElement>, node: TreeDataNode) => void;
  onMouseDown?: (key: string) => void;
  onMouseUp?: (key: string) => void;
  onHover?: (key: string) => void;
  sharedPaths?: Set<string>;
  shareInfoMap?: Map<string, SharedWithMeEntry>;
}

const FileTreeTitleRenderer: React.FC<FileTreeTitleRendererProps> = ({
  node,
  selectedKeys,
  expandedKeys,
  openFolder,
  isFileNew,
  doesFolderContainNewFiles,
  setFileMenuVisibleKey,
  handleCreateDialogOpen,
  setUploadDialogVisible,
  handleDrop,
  handleDragOverInTree,
  onHover = () => { },
  onMouseDown = () => { },
  onMouseUp = () => { },
  sharedPaths,
  shareInfoMap,
}) => {
  const { message } = App.useApp();
  // 检查是否为新创建的文件或文件夹
  const isNew = isFileNew(node.key as string);
  // 检查文件夹是否包含新文件（只对文件夹有效）
  const folderHasNewFiles = node.isDir && doesFolderContainNewFiles(node.key as string);
  // 确定文字颜色类名
  let textColorClass = '';
  if (selectedKeys.includes(node.key)) {
    textColorClass = 'text-primary';
  } else if (isNew) {
    textColorClass = 'text-green-600 dark:text-[#89D185]'; // 新文件和新文件夹显示为绿色
  } else if (folderHasNewFiles) {
    textColorClass = 'text-yellow-600 dark:text-[#CCA700]'; // 包含新文件的文件夹显示为黄色
  }

  const isShared = sharedPaths?.has(node.key as string);
  const shareInfo = isShared ? shareInfoMap?.get(node.key as string) : undefined;
  const shareType = shareInfo?.share_type;
  const sharingUser = shareInfo?.sharing_user;
  const sharedFileName = shareInfo?.file_name;
  const shareColorMap: Record<string, string> = { read: '#1677ff', write: '#fa8c16', full: '#52c41a' };
  const shareColor = shareColorMap[shareType || ''] || '#1677ff';
  const shareTooltip = sharingUser
    ? `Shared by ${sharingUser}${shareType ? ` (${shareType})` : ''}`
    : shareType ? `Shared (${shareType})` : 'Shared';

  const renamedKey = useFileStore(state => state.renamedKey);

  const setRenamedKey = useFileStore(state => state.setRenamedKey);

  const renameFile = useFileStore((state) => state.renameFile);

  const { t } = useTranslation('common');

  const onRenameBlur = async (e: any) => {
    const newName = e.target.value.trim();
    if (newName && newName !== node.name) {
      try {
        const isSuccess = await renameFile(node.key, newName);
        if (!isSuccess) {
          message.error(t('file_manager.rename_failed'));
        }
      } catch (error) {
        console.error(`Failed to rename:`, error);
      } finally {
        setRenamedKey(undefined);
      }
    } else {
      setRenamedKey(undefined);
    }
  }


  const ref = useRef(null);
  const isHovering = useHover(ref);

  // dnd-kit hooks
  const { attributes, listeners, setNodeRef: setDragNodeRef, isDragging } = useDraggable({
    id: node.key as string,
    disabled: renamedKey === node.key,
  });

  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
    id: node.key as string,
    disabled: renamedKey === node.key,
  });

  const expandTimerRef = useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }

    if (isOver && node.isDir && !expandedKeys.includes(node.key as string)) {
      expandTimerRef.current = setTimeout(() => {
        openFolder(node.key as string);
      }, 500);
    }

    return () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }
    };
  }, [isOver, node.isDir, node.key, expandedKeys, openFolder]);

  // Combine refs
  const setNodeRef = (element: HTMLDivElement | null) => {
    setDragNodeRef(element);
    setDropNodeRef(element);
    (ref as any).current = element;
  };

  const createFileDialogHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFileMenuVisibleKey(node.key as string);
    handleCreateDialogOpen('file');
  };

  const createFolderDialogHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFileMenuVisibleKey(node.key as string);
    handleCreateDialogOpen('folder');
  };

  const uploadDialogHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFileMenuVisibleKey(node.key as string);
    setUploadDialogVisible(true);
  };


  const onMouseUpHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.button === 0) {
      onMouseUp(node.key as string);
    }
  }

  const onMouseDownHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.button === 0) {
      onMouseDown(node.key as string);
    }
  }

  return (
    <div
      id={"fileItem" + node.key}
      onMouseEnter={() => onHover(node.key)}
      onMouseUp={onMouseUpHandler}
      onMouseDown={onMouseDownHandler}
      onDrop={(e) => handleDrop(e, node)}
      onDragOver={(e) => handleDragOverInTree(e, node)}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex w-full min-w-0 flex-row items-center justify-between select-none ${isDragging ? 'opacity-50' : ''} ${isOver && node.isDir ? 'bg-blue-100 dark:bg-[#0E639C]' : ''}`}
      data-key={node.key}
      style={{
        cursor: isDragging ? 'grabbing' : 'pointer',
        touchAction: 'none',
      }}
    >
      {isShared && (
        <ShareAltOutlined style={{ color: shareColor, fontSize: 11, width: '8px', marginRight: '8px', marginBottom: '-2px' }} />
      )}
      <div className='flex min-w-0 flex-1 items-center gap-1'>
        {
          renamedKey === node.key ?
            <Input defaultValue={node?.name} autoFocus onPressEnter={onRenameBlur} onBlur={onRenameBlur} id={`rename-${node.key}`} size="small" />
            :
            <span
              className={`${textColorClass} block min-w-0 flex-1 truncate select-none`}
              title={node?.name}
              style={{ color: isShared ? shareColor : undefined }}
            >
              {node?.name}
            </span>
        }
        <div
          style={textColorClass.includes('text-yellow-600') ? { opacity: 0.6 } : { opacity: 0 }}
          className='w-2 h-2 bg-yellow-600 rounded-full inline-block'
        />
      </div>
      {isHovering && node.isDir && (
        <div className='flex gap-2 pr-6'>
          <Button
            size='small'
            shape='circle'
            className='text-xs'
            title='Rename'
            onClick={() => {
              setRenamedKey(node.key as string)
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <EditOutlined />
          </Button>
          <Button
            size='small'
            shape='circle'
            className='text-xs'
            title='New File'
            onClick={createFileDialogHandler}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <FileAddOutlined />
          </Button>
          <Button
            size='small'
            shape='circle'
            className='text-xs'
            title='New Folder'
            onMouseDown={(e) => e.stopPropagation()}
            onClick={createFolderDialogHandler}
          >
            <FolderAddOutlined />
          </Button>
          <Button
            size='small'
            shape='circle'
            className='text-xs'
            title='Upload'
            onClick={uploadDialogHandler}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <UploadOutlined />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileTreeTitleRenderer;
